import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  DefList,
  ErrorState,
  Icon,
  Loading,
  Pill,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import {
  dateTime,
  longDate,
  money,
  pricingLabel,
  rate,
  shortDate,
  stageLabel,
  stageTone,
} from '../lib/format';
import type {
  ApplicationDetail,
  BoardingGap,
  BoardingState,
} from '../lib/types';
import { ApplicationWizard } from './NewApplication';

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useApi<ApplicationDetail>(
    id ? `/applications/${id}` : null,
  );
  const [local, setLocal] = useState<ApplicationDetail | null>(null);
  const app = local ?? data;

  if (loading && !app) {
    return (
      <>
        <PageHeader breadcrumb={['Applications']} title="Loading…" />
        <PageBody>
          <Loading />
        </PageBody>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader breadcrumb={['Applications']} title="Application" />
        <PageBody>
          <ErrorState error={error} onRetry={reload} />
        </PageBody>
      </>
    );
  }

  if (!app) return null;

  // A draft is still being written, so it opens in the wizard rather than the
  // read-only review view.
  if (app.stage === 'draft') {
    return <ApplicationWizard app={app} onSaved={setLocal} />;
  }

  return <ReviewView app={app} onChanged={setLocal} reload={reload} />;
}

function ReviewView({
  app,
  onChanged,
  reload,
}: {
  app: ApplicationDetail;
  onChanged: (a: ApplicationDetail) => void;
  reload: () => void;
}) {
  const { user, can } = useAuth();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [ceiling, setCeiling] = useState(
    app.decision?.approvedMonthlyCeilingCents ??
      Math.round(app.processing.monthlyVolumeCents * 1.3),
  );
  const [reserve, setReserve] = useState<'none' | 'rolling_5_180' | 'capped_10000'>(
    'none',
  );
  const [declineReason, setDeclineReason] = useState('');

  const received = app.documents.filter((d) => d.received).length;

  async function run<T>(work: Promise<T>) {
    setBusy(true);
    setErr(null);
    try {
      const next = (await work) as ApplicationDetail;
      if (next && typeof next === 'object' && 'stage' in next) onChanged(next);
      else reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={[
          can('application.decide') ? 'Underwriting' : 'Applications',
          app.reference,
          app.submittedAt ? `received ${shortDate(app.submittedAt)}` : 'not submitted',
        ]}
        title={app.business.dba || app.business.legalName || 'Untitled'}
        actions={
          <>
            <Pill tone={stageTone(app.stage)}>{stageLabel(app.stage)}</Pill>
            <Button>Print packet</Button>
          </>
        }
      />

      <ScopeBar>
        Submitted by <strong>{app.submittedByName}</strong> ({app.submittedByLevel}).
        {can('application.see_decline_reason') ? (
          <> Decline reasons stay visible to L2 and L3 only.</>
        ) : (
          <> Decline reasons are not shown at your level.</>
        )}
      </ScopeBar>

      <PageBody>
        {err && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{err}</span>
          </div>
        )}

        <div className="split-2">
          <div className="stack">
            {app.riskSignals && (
              <Card>
                <CardHeader
                  title="Risk signals"
                  meta={`scored ${dateTime(app.riskSignals.scoredAt)}`}
                  action={
                    <Pill
                      tone={
                        app.riskSignals.band === 'low'
                          ? 'ok'
                          : app.riskSignals.band === 'high'
                            ? 'danger'
                            : 'warn'
                      }
                    >
                      {app.riskSignals.band} · {app.riskSignals.score}/100
                    </Pill>
                  }
                />
                <div className="tilerow">
                  <SignalTile
                    label="Credit — control person"
                    value={String(app.riskSignals.creditScore ?? '—')}
                    note={
                      (app.riskSignals.creditScore ?? 0) >= 680
                        ? 'Above 680 floor'
                        : 'Below 680 floor'
                    }
                    tone={(app.riskSignals.creditScore ?? 0) >= 680 ? 'ok' : 'danger'}
                  />
                  <SignalTile
                    label="MATCH / TMF"
                    value={app.riskSignals.matchClear ? 'Clear' : 'Listed'}
                    note={
                      app.riskSignals.matchClear ? 'No listing found' : 'Listing found'
                    }
                    tone={app.riskSignals.matchClear ? 'ok' : 'danger'}
                  />
                  <SignalTile
                    label="Avg ticket vs MCC"
                    value={money(app.processing.averageTicketCents)}
                    note={`${app.riskSignals.averageTicketVsMccMedian ?? '—'}× MCC median`}
                    tone={
                      (app.riskSignals.averageTicketVsMccMedian ?? 0) > 2 ? 'warn' : 'ok'
                    }
                  />
                  <SignalTile
                    label="Site verification"
                    value={app.riskSignals.siteVerified ? 'Passed' : 'Pending'}
                    note={`Photos by ${app.submittedByName}`}
                    tone={app.riskSignals.siteVerified ? 'ok' : 'warn'}
                  />
                </div>
              </Card>
            )}

            <Card>
              <CardHeader
                title="Application packet"
                meta={`${received} of ${app.documents.length}`}
              />
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {app.documents.map((d) => (
                      <tr key={d.kind}>
                        <td style={{ width: 24 }}>
                          <span
                            className={`check__icon check__icon--${d.received ? 'pass' : 'fail'}`}
                          >
                            {d.received ? Icon.check({ size: 11 }) : Icon.alert({ size: 11 })}
                          </span>
                        </td>
                        <td>
                          <div className="cell-primary">{d.label}</div>
                          {d.detail && <div className="check__detail">{d.detail}</div>}
                        </td>
                        <td>
                          {d.received ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() =>
                                void run(
                                  api.patch(`/applications/${app.id}/documents`, {
                                    kind: d.kind,
                                    received: false,
                                  }),
                                )
                              }
                            >
                              Mark outstanding
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              disabled={busy}
                              onClick={() =>
                                void run(
                                  api.patch(`/applications/${app.id}/documents`, {
                                    kind: d.kind,
                                    received: true,
                                    detail: `Marked received by ${user?.name}`,
                                  }),
                                )
                              }
                            >
                              Mark received
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card>
              <CardHeader title="Review thread" count={app.reviewThread.length} />
              {app.reviewThread.length === 0 ? (
                <p className="card__meta">Nothing posted on this file yet.</p>
              ) : (
                <div className="thread">
                  {app.reviewThread.map((t) => (
                    <div className="thread__item" key={t.id}>
                      <Avatar
                        initials={t.authorName
                          .split(' ')
                          .map((p) => p[0])
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()}
                        size={28}
                        tone="muted"
                      />
                      <div style={{ minWidth: 0 }}>
                        <div className="thread__meta">
                          <strong>{t.authorName}</strong> · {t.authorRole} ·{' '}
                          {dateTime(t.createdAt)}
                        </div>
                        <p className="thread__body">{t.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <form
                className="thread__compose"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!note.trim()) return;
                  void run(
                    api
                      .post<ApplicationDetail>(`/applications/${app.id}/notes`, {
                        body: note.trim(),
                      })
                      .then((r) => {
                        setNote('');
                        return r;
                      }),
                  );
                }}
              >
                <div className="field">
                  <label className="field__label" htmlFor="note">
                    Add a note to the review thread
                  </label>
                  <input
                    id="note"
                    className="input"
                    placeholder="Visible to L1–L3 on this file"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <Button type="submit" variant="primary" disabled={busy || !note.trim()}>
                  Post
                </Button>
              </form>
            </Card>
          </div>

          <div className="stack">
            {can('application.decide') && !app.decision && (
              <Card>
                <CardHeader title="Decision" />
                <div className="stack" style={{ gap: 10 }}>
                  <Button
                    variant="primary"
                    block
                    disabled={busy}
                    onClick={() =>
                      void run(
                        api.post(`/applications/${app.id}/decision`, {
                          outcome: 'approved',
                          approvedMonthlyCeilingCents: ceiling,
                          reserve,
                        }),
                      )
                    }
                  >
                    Approve and issue MID
                  </Button>
                  <Button
                    block
                    disabled={busy}
                    onClick={() =>
                      void run(
                        api.post(`/applications/${app.id}/decision`, {
                          outcome: 'approved_with_conditions',
                          approvedMonthlyCeilingCents: ceiling,
                          reserve,
                          conditions: app.documents
                            .filter((d) => !d.received)
                            .map((d) => d.label),
                        }),
                      )
                    }
                  >
                    Approve with conditions
                  </Button>
                  <div className="row" style={{ gap: 8 }}>
                    <Button
                      style={{ flexGrow: 1 }}
                      disabled={busy}
                      onClick={() =>
                        void run(
                          api.post(`/applications/${app.id}/decision`, {
                            outcome: 'returned',
                            reason: 'Returned to the agent for correction.',
                          }),
                        )
                      }
                    >
                      Return to agent
                    </Button>
                    <Button
                      variant="danger"
                      style={{ flexGrow: 1 }}
                      disabled={busy || !declineReason.trim()}
                      title={
                        declineReason.trim() ? undefined : 'A decline needs a reason first'
                      }
                      onClick={() =>
                        void run(
                          api.post(`/applications/${app.id}/decision`, {
                            outcome: 'declined',
                            reason: declineReason.trim(),
                          }),
                        )
                      }
                    >
                      Decline
                    </Button>
                  </div>

                  <div className="field">
                    <label className="field__label">Approved monthly ceiling</label>
                    <input
                      className="input"
                      value={(ceiling / 100).toFixed(0)}
                      onChange={(e) =>
                        setCeiling(Math.round(Number(e.target.value.replace(/\D/g, '')) * 100))
                      }
                    />
                  </div>
                  <div className="field">
                    <label className="field__label">Reserve</label>
                    <select
                      className="select"
                      value={reserve}
                      onChange={(e) => setReserve(e.target.value as typeof reserve)}
                    >
                      <option value="none">None</option>
                      <option value="rolling_5_180">Rolling 5% for 180 days</option>
                      <option value="capped_10000">Capped $10,000</option>
                    </select>
                  </div>
                  <div className="field">
                    <label className="field__label">
                      Decline reason — never shown to the writing agent
                    </label>
                    <textarea
                      className="textarea"
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      placeholder="Required only for a decline"
                    />
                  </div>
                </div>
              </Card>
            )}

            {app.stage === 'rate_exception' && can('application.price_below_floor') && (
              <Card>
                <CardHeader title="Rate exception" />
                <p className="card__meta" style={{ marginBottom: 12 }}>
                  This schedule sits below the writing node’s inherited floor and is
                  waiting on a parent office.
                </p>
                <Button
                  variant="primary"
                  block
                  disabled={busy}
                  onClick={() =>
                    void run(api.post(`/applications/${app.id}/rate-exception/approve`))
                  }
                >
                  Approve the exception
                </Button>
              </Card>
            )}

            {app.decision && (
              <Card>
                <CardHeader title="Decision" />
                <DefList
                  items={[
                    {
                      term: 'Outcome',
                      value: (
                        <Pill
                          tone={
                            app.decision.outcome === 'declined'
                              ? 'danger'
                              : app.decision.outcome === 'returned'
                                ? 'warn'
                                : 'ok'
                          }
                        >
                          {app.decision.outcome.replace(/_/g, ' ')}
                        </Pill>
                      ),
                    },
                    { term: 'By', value: app.decision.decidedByName },
                    { term: 'On', value: longDate(app.decision.decidedAt) },
                    ...(app.mid ? [{ term: 'MID', value: <span className="mono">{app.mid}</span> }] : []),
                  ]}
                />
                {app.decision.reason ? (
                  <div className="notice notice--danger" style={{ marginTop: 12 }}>
                    {Icon.alert({ size: 15 })}
                    <span>{app.decision.reason}</span>
                  </div>
                ) : app.decision.outcome === 'declined' ? (
                  <div className="notice notice--warn" style={{ marginTop: 12 }}>
                    {Icon.alert({ size: 15 })}
                    <span>
                      The reason for this decline is visible to L2 and L3 only.
                    </span>
                  </div>
                ) : null}
              </Card>
            )}

            {(app.stage === 'approved' || app.boarding) && (
              <BoardingCard app={app} onChanged={onChanged} />
            )}

            <Card>
              <CardHeader title="Approval chain" />
              <ApprovalChain app={app} />
            </Card>

            <Card>
              <CardHeader title="Requested terms" />
              <DefList
                items={[
                  {
                    term: 'Schedule',
                    value: pricingLabel(app.pricing.model, app.pricing.basisPoints),
                  },
                  { term: 'Volume', value: `${money(app.processing.monthlyVolumeCents)} / mo` },
                  { term: 'Average ticket', value: money(app.processing.averageTicketCents) },
                  {
                    term: 'Card mix',
                    value: `${app.processing.cardPresentPercent}% present`,
                  },
                  {
                    term: 'Equipment',
                    value:
                      app.equipment.length > 0
                        ? app.equipment
                            .map((e) => `${e.quantity} × ${e.name}`)
                            .join(', ')
                        : 'None',
                  },
                  { term: 'MCC', value: `${app.business.mcc} — ${app.business.mccDescription}` },
                ]}
              />
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

/**
 * MX Connect boarding.
 *
 * Shown once a file is approved, which is the point the portal has done all it
 * can and the merchant has to reach the processor. The panel is deliberately
 * explicit about the two fields MX collects itself: an underwriter who sees
 * "filed as a draft" needs to know whose move it is next.
 */
function BoardingCard({
  app,
  onChanged,
}: {
  app: ApplicationDetail;
  onChanged: (next: ApplicationDetail) => void;
}) {
  const { can } = useAuth();
  const [state, setState] = useState<BoardingState | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [failures, setFailures] = useState<BoardingGap[]>([]);

  const run = state?.submission ?? app.boarding;
  const gaps = state?.gaps ?? [];
  const configured = state?.configured ?? true;

  // The panel's own state is fetched once the card mounts, so the gaps are
  // current even on a file nobody has tried to board yet.
  useEffect(() => {
    let cancelled = false;
    api
      .get<BoardingState>(`/applications/${app.id}/mxconnect`)
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [app.id]);

  async function submit() {
    setBusy(true);
    setErr(null);
    setFailures([]);
    try {
      const next = await api.post<BoardingState>(
        `/applications/${app.id}/mxconnect/submit`,
      );
      setState(next);
      onChanged({ ...app, stage: next.stage, boarding: next.submission });
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e.message);
        // A preflight refusal arrives in the same shape the pipeline's gate
        // checks use, so it renders as a list rather than one long sentence.
        setFailures(
          e.failures.map((f) => ({
            id: f.id,
            label: f.label,
            detail: f.detail ?? '',
          })),
        );
        const body = e.body as { validations?: string[] } | null;
        if (body?.validations) {
          setFailures(
            body.validations.map((v, i) => ({ id: String(i), label: v, detail: '' })),
          );
        }
      } else {
        setErr('That did not work.');
      }
    } finally {
      setBusy(false);
    }
  }

  const tone =
    run?.status === 'submitted'
      ? 'ok'
      : run?.status === 'filed_draft'
        ? 'warn'
        : run?.status === 'failed'
          ? 'danger'
          : 'neutral';

  const label =
    run?.status === 'submitted'
      ? 'Submitted'
      : run?.status === 'filed_draft'
        ? 'Draft filed'
        : run?.status === 'failed'
          ? 'Rejected'
          : 'Not sent';

  return (
    <Card>
      <CardHeader
        title="MX Connect"
        meta={run?.accountStatus ? `MX status: ${run.accountStatus}` : undefined}
        action={<Pill tone={tone}>{label}</Pill>}
      />

      <div className="stack" style={{ gap: 12 }}>
        {!configured && (
          <div className="notice notice--warn">
            {Icon.alert({ size: 15 })}
            <span>
              MX Connect is not configured on this API. Set MXCONNECT_API_KEY and
              MXCONNECT_DOMAIN_ID.
            </span>
          </div>
        )}

        {err && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{err}</span>
          </div>
        )}

        {failures.length > 0 && (
          <ul className="bullets">
            {failures.map((f) => (
              <li key={f.id}>
                <strong>{f.label}</strong>
                {f.detail ? <> — {f.detail}</> : null}
              </li>
            ))}
          </ul>
        )}

        {run?.status === 'filed_draft' && (
          <p className="muted" style={{ margin: 0 }}>
            The whole application is at MX Connect as a draft. It cannot be
            submitted for underwriting until these are entered there — this
            portal stores only the last four digits of each:
          </p>
        )}

        {run?.pendingAtMx && run.pendingAtMx.length > 0 && (
          <ul className="bullets">
            {run.pendingAtMx.map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        )}

        {run?.lastError && run.status === 'failed' && (
          <p className="muted" style={{ margin: 0 }}>
            Failed at <strong>{run.lastError.step.replace(/_/g, ' ')}</strong> —{' '}
            {run.lastError.message}
          </p>
        )}

        {run?.uarId && (
          <DefList
            items={[
              { term: 'UAR', value: run.uarId },
              { term: 'Account', value: run.accountId ?? '—' },
              {
                term: 'Sent',
                value: run.submittedAt
                  ? `${dateTime(run.submittedAt)} · ${run.submittedByName ?? ''}`
                  : '—',
              },
            ]}
          />
        )}

        {!run && gaps.length > 0 && (
          <>
            <p className="muted" style={{ margin: 0 }}>
              MX Connect will need:
            </p>
            <ul className="bullets">
              {gaps.map((g) => (
                <li key={g.id}>
                  <strong>{g.label}</strong> — {g.detail}
                </li>
              ))}
            </ul>
          </>
        )}

        {can('application.board') && run?.status !== 'submitted' && (
          <Button
            variant="primary"
            block
            disabled={busy || !configured}
            onClick={() => void submit()}
          >
            {busy
              ? 'Sending…'
              : run
                ? 'Retry submission'
                : 'Submit to MX Connect'}
          </Button>
        )}

        {!can('application.board') && (
          <p className="muted" style={{ margin: 0 }}>
            Boarding is an underwriting action; your role cannot submit to the
            processor.
          </p>
        )}
      </div>
    </Card>
  );
}

function SignalTile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone: 'ok' | 'warn' | 'danger';
}) {
  return (
    <div className="tile">
      <div className="tile__label">{label}</div>
      <div className="tile__value-row">
        <span className="tile__value" style={{ fontSize: 22 }}>
          {value}
        </span>
      </div>
      <div style={{ marginTop: 8 }}>
        <Pill tone={tone}>{note}</Pill>
      </div>
    </div>
  );
}

/** Who has touched the file and who holds it now. */
function ApprovalChain({ app }: { app: ApplicationDetail }) {
  const decided = Boolean(app.decision);
  const steps = [
    {
      title: `${app.submittedByName} · ${app.submittedByLevel}`,
      sub: app.submittedAt
        ? `Submitted ${shortDate(app.submittedAt)}`
        : 'Not yet submitted',
      state: app.submittedAt ? 'done' : 'todo',
    },
    {
      title: 'Writing office',
      sub: app.pricing.belowFloor
        ? app.pricing.exceptionApprovedByNodeId
          ? 'Rate exception cleared'
          : 'Rate exception pending'
        : 'Pricing within schedule — auto-passed',
      state: app.pricing.belowFloor && !app.pricing.exceptionApprovedByNodeId ? 'current' : 'done',
    },
    {
      title: 'Fairmount Underwriting · L1',
      sub: decided
        ? `Decided ${shortDate(app.decision!.decidedAt)}`
        : app.stage === 'in_underwriting'
          ? 'With underwriting'
          : app.stage === 'conditions'
            ? 'Awaiting conditions'
            : 'Not yet reached',
      state: decided ? 'done' : app.stage === 'in_underwriting' || app.stage === 'conditions' ? 'current' : 'todo',
    },
  ] as const;

  return (
    <div className="chain">
      {steps.map((s, i) => (
        <div className="chain__step" key={s.title}>
          <div className="chain__rail">
            <span className={`chain__dot chain__dot--${s.state}`}>
              {s.state === 'done' ? Icon.check({ size: 11 }) : null}
            </span>
            {i < steps.length - 1 && <span className="chain__line" />}
          </div>
          <div className="chain__body">
            <div className="chain__title">{s.title}</div>
            <div className="chain__sub">{s.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export { rate };
